import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/DemographicSurvey.css";
import Navbar from "../components/Navbar";

const DemographicSurvey = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    student_id: "",
    full_name: "",
    institutional_email: "",
    personal_email: "",
    sex: "",
    other_sex: "",
    age_range: "",
    state: "",
    university_type: "",
    knowledge_area: "",
    shift: "",
    semester: "",
    wants_to_work: "",
    other_work: "",
    wants_ai_robotics: "",
    other_ai_robotics: ""
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const states = [
    "Aguascalientes", "Baja California", "Baja California Sur", "Campeche", "Chiapas",
    "Chihuahua", "Ciudad de México", "Coahuila", "Colima", "Durango", "Estado de México",
    "Guanajuato", "Guerrero", "Hidalgo", "Jalisco", "Michoacán", "Morelos", "Nayarit",
    "Nuevo León", "Oaxaca", "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí",
    "Sinaloa", "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas"
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "radio") {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Limpiar error cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.student_id.trim()) {
      newErrors.student_id = "El número de boleta es requerido";
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = "El nombre completo es requerido";
    }

    if (!formData.institutional_email.trim() && !formData.personal_email.trim()) {
      newErrors.email = "Debe proporcionar al menos un correo electrónico";
    }

    if (formData.institutional_email.trim() && !/\S+@\S+\.\S+/.test(formData.institutional_email)) {
      newErrors.institutional_email = "El correo institucional no es válido";
    }

    if (formData.personal_email.trim() && !/\S+@\S+\.\S+/.test(formData.personal_email)) {
      newErrors.personal_email = "El correo personal no es válido";
    }

    if (!formData.sex) {
      newErrors.sex = "Debe seleccionar una opción de sexo";
    }

    if (formData.sex === "Otro" && !formData.other_sex.trim()) {
      newErrors.other_sex = "Debe especificar el sexo";
    }

    if (!formData.age_range) {
      newErrors.age_range = "Debe seleccionar un rango de edad";
    }

    if (!formData.state) {
      newErrors.state = "Debe seleccionar un estado";
    }

    if (!formData.university_type) {
      newErrors.university_type = "Debe seleccionar el tipo de universidad";
    }

    if (!formData.knowledge_area) {
      newErrors.knowledge_area = "Debe seleccionar un área de conocimiento";
    }

    if (!formData.shift) {
      newErrors.shift = "Debe seleccionar un turno";
    }

    if (!formData.semester) {
      newErrors.semester = "Debe seleccionar un semestre";
    }

    if (!formData.wants_to_work) {
      newErrors.wants_to_work = "Debe responder sobre el trabajo";
    }

    if (formData.wants_to_work === "Otra" && !formData.other_work.trim()) {
      newErrors.other_work = "Debe especificar la respuesta";
    }

    if (!formData.wants_ai_robotics) {
      newErrors.wants_ai_robotics = "Debe responder sobre IA y robótica";
    }

    if (formData.wants_ai_robotics === "Otra" && !formData.other_ai_robotics.trim()) {
      newErrors.other_ai_robotics = "Debe especificar la respuesta";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/submit-demographic`,
        formData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Respuesta del servidor:", response.data);
      navigate("/demographic-results");
    } catch (error) {
      console.error("Error al enviar el formulario:", error);
      alert("Error al enviar el formulario. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="dashboard-layout">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          <div className="sidebar-header">
            <h3>Datos Generales del Estudiante</h3>
          </div>
          <div className="sidebar-menu">
            <button
              className="menu-item active"
            >
              <i className="bi bi-person-lines-fill"></i> Cuestionario
            </button>
            <button
              className="menu-item return-dashboard"
              onClick={() => navigate("/dashboard")}
            >
              <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
            </button>
          </div>
        </div>

        {/* Main */}
        <div className="dashboard-main">
          <div className="survey-container">
            <div className="survey-header">
              <h2>Cuestionario de Datos Generales del Estudiante</h2>
              <p className="survey-description">
                Este cuestionario tiene como propósito recopilar información básica de los estudiantes para
                conocer su perfil académico, intereses y disposición para integrarse en proyectos de formación,
                innovación y vinculación laboral.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="demographic-form">
              {/* Pregunta 1: Número de boleta */}
              <div className="form-group">
                <label htmlFor="student_id" className="form-label">
                  1. Número de boleta: <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="student_id"
                  name="student_id"
                  value={formData.student_id}
                  onChange={handleInputChange}
                  className={`form-input ${errors.student_id ? 'error' : ''}`}
                  placeholder="Ingresa tu número de boleta"
                />
                {errors.student_id && <span className="error-message">{errors.student_id}</span>}
              </div>

              {/* Pregunta 2: Nombre completo */}
              <div className="form-group">
                <label htmlFor="full_name" className="form-label">
                  2. Nombre completo: <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className={`form-input ${errors.full_name ? 'error' : ''}`}
                  placeholder="Ingresa tu nombre completo"
                />
                {errors.full_name && <span className="error-message">{errors.full_name}</span>}
              </div>

              {/* Pregunta 3: Correos electrónicos */}
              <div className="form-group">
                <label className="form-label">
                  3. Correo electrónico: <span className="required">*</span>
                </label>
                <div className="email-group">
                  <div className="email-input-group">
                    <label htmlFor="institutional_email" className="email-label">
                      Institucional:
                    </label>
                    <input
                      type="email"
                      id="institutional_email"
                      name="institutional_email"
                      value={formData.institutional_email}
                      onChange={handleInputChange}
                      className={`form-input ${errors.institutional_email ? 'error' : ''}`}
                      placeholder="correo@institucion.edu.mx"
                    />
                    {errors.institutional_email && <span className="error-message">{errors.institutional_email}</span>}
                  </div>
                  <div className="email-input-group">
                    <label htmlFor="personal_email" className="email-label">
                      Personal:
                    </label>
                    <input
                      type="email"
                      id="personal_email"
                      name="personal_email"
                      value={formData.personal_email}
                      onChange={handleInputChange}
                      className={`form-input ${errors.personal_email ? 'error' : ''}`}
                      placeholder="correo@personal.com"
                    />
                    {errors.personal_email && <span className="error-message">{errors.personal_email}</span>}
                  </div>
                </div>
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              {/* Pregunta 4: Sexo */}
              <div className="form-group">
                <label className="form-label">
                  4. Sexo: <span className="required">*</span>
                </label>
                <div className="radio-group">
                  {["Femenino", "Masculino", "Otro"].map((option) => (
                    <label key={option} className="radio-label">
                      <input
                        type="radio"
                        name="sex"
                        value={option}
                        checked={formData.sex === option}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">{option}</span>
                    </label>
                  ))}
                </div>
                {formData.sex === "Otro" && (
                  <input
                    type="text"
                    name="other_sex"
                    value={formData.other_sex}
                    onChange={handleInputChange}
                    className={`form-input ${errors.other_sex ? 'error' : ''}`}
                    placeholder="Especifica tu sexo"
                  />
                )}
                {errors.sex && <span className="error-message">{errors.sex}</span>}
                {errors.other_sex && <span className="error-message">{errors.other_sex}</span>}
              </div>

              {/* Pregunta 5: Rango de edad */}
              <div className="form-group">
                <label className="form-label">
                  5. Rango de edad: <span className="required">*</span>
                </label>
                <div className="radio-group">
                  {[
                    "Menos de 18 años",
                    "18 – 20 años",
                    "21 – 23 años",
                    "24 – 26 años",
                    "27 – 29 años",
                    "30 años o más"
                  ].map((option) => (
                    <label key={option} className="radio-label">
                      <input
                        type="radio"
                        name="age_range"
                        value={option}
                        checked={formData.age_range === option}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.age_range && <span className="error-message">{errors.age_range}</span>}
              </div>

              {/* Pregunta 6: Estado */}
              <div className="form-group">
                <label htmlFor="state" className="form-label">
                  6. Estado al que perteneces: <span className="required">*</span>
                </label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`form-select ${errors.state ? 'error' : ''}`}
                >
                  <option value="">Selecciona un estado</option>
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
                {errors.state && <span className="error-message">{errors.state}</span>}
              </div>

              {/* Pregunta 7: Tipo de universidad */}
              <div className="form-group">
                <label className="form-label">
                  7. Tipo de universidad: <span className="required">*</span>
                </label>
                <div className="radio-group">
                  {["Pública", "Privada"].map((option) => (
                    <label key={option} className="radio-label">
                      <input
                        type="radio"
                        name="university_type"
                        value={option}
                        checked={formData.university_type === option}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.university_type && <span className="error-message">{errors.university_type}</span>}
              </div>

              {/* Pregunta 8: Área de conocimiento */}
              <div className="form-group">
                <label className="form-label">
                  8. Área de conocimiento: <span className="required">*</span>
                </label>
                <div className="radio-group">
                  {[
                    "Ciencias Sociales y Administrativas",
                    "Ciencias de la Salud",
                    "Ingenierías y Tecnologías",
                    "Ciencias Naturales y Exactas",
                    "Humanidades y Artes"
                  ].map((option) => (
                    <label key={option} className="radio-label">
                      <input
                        type="radio"
                        name="knowledge_area"
                        value={option}
                        checked={formData.knowledge_area === option}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.knowledge_area && <span className="error-message">{errors.knowledge_area}</span>}
              </div>

              {/* Pregunta 9: Turno */}
              <div className="form-group">
                <label className="form-label">
                  9. Turno: <span className="required">*</span>
                </label>
                <div className="radio-group">
                  {["Matutino", "Vespertino", "Nocturno"].map((option) => (
                    <label key={option} className="radio-label">
                      <input
                        type="radio"
                        name="shift"
                        value={option}
                        checked={formData.shift === option}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.shift && <span className="error-message">{errors.shift}</span>}
              </div>

              {/* Pregunta 10: Semestre */}
              <div className="form-group">
                <label className="form-label">
                  10. Semestre que cursa: <span className="required">*</span>
                </label>
                <div className="radio-group">
                  {["1°", "2°", "3°", "4°", "5°", "6°", "7°", "8°", "9° o más"].map((option) => (
                    <label key={option} className="radio-label">
                      <input
                        type="radio"
                        name="semester"
                        value={option}
                        checked={formData.semester === option}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.semester && <span className="error-message">{errors.semester}</span>}
              </div>

              {/* Pregunta 11: ¿Quieres trabajar? */}
              <div className="form-group">
                <label className="form-label">
                  11. ¿Quieres trabajar?: <span className="required">*</span>
                </label>
                <div className="radio-group">
                  {[
                    "Sí, de inmediato",
                    "Sí, pero después de terminar mis estudios",
                    "No, por el momento",
                    "Otra"
                  ].map((option) => (
                    <label key={option} className="radio-label">
                      <input
                        type="radio"
                        name="wants_to_work"
                        value={option}
                        checked={formData.wants_to_work === option}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">{option}</span>
                    </label>
                  ))}
                </div>
                {formData.wants_to_work === "Otra" && (
                  <input
                    type="text"
                    name="other_work"
                    value={formData.other_work}
                    onChange={handleInputChange}
                    className={`form-input ${errors.other_work ? 'error' : ''}`}
                    placeholder="Especifica tu respuesta"
                  />
                )}
                {errors.wants_to_work && <span className="error-message">{errors.wants_to_work}</span>}
                {errors.other_work && <span className="error-message">{errors.other_work}</span>}
              </div>

              {/* Pregunta 12: ¿Te gustaría participar en proyectos de IA y robótica? */}
              <div className="form-group">
                <label className="form-label">
                  12. ¿Te gustaría participar en proyectos de inteligencia artificial y robótica?: <span className="required">*</span>
                </label>
                <div className="radio-group">
                  {[
                    "Sí, de inmediato",
                    "Sí, pero necesito capacitación previa",
                    "No, por el momento",
                    "Otra"
                  ].map((option) => (
                    <label key={option} className="radio-label">
                      <input
                        type="radio"
                        name="wants_ai_robotics"
                        value={option}
                        checked={formData.wants_ai_robotics === option}
                        onChange={handleInputChange}
                      />
                      <span className="radio-text">{option}</span>
                    </label>
                  ))}
                </div>
                {formData.wants_ai_robotics === "Otra" && (
                  <input
                    type="text"
                    name="other_ai_robotics"
                    value={formData.other_ai_robotics}
                    onChange={handleInputChange}
                    className={`form-input ${errors.other_ai_robotics ? 'error' : ''}`}
                    placeholder="Especifica tu respuesta"
                  />
                )}
                {errors.wants_ai_robotics && <span className="error-message">{errors.wants_ai_robotics}</span>}
                {errors.other_ai_robotics && <span className="error-message">{errors.other_ai_robotics}</span>}
              </div>

              {/* Botones */}
              <div className="form-actions text-center mt-4">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="btn btn-secondary me-3"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? "Enviando..." : "Enviar Cuestionario"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemographicSurvey;
